import { useCallback, useEffect, useMemo, useState } from 'react';

import DraftCard from '../components/DraftCard';
import PlatformIcon from '../components/PlatformIcon';
import QueueTimeline from '../components/QueueTimeline';
import type { ActionResult } from '../lib/actions';
import { markSent, queueForAutoPost, removeDraft, reopenDraft, saveDraftBody, unqueueAutoPost } from '../lib/actions';
import { redraftDraft } from '../lib/brain';
import { platformLabel } from '../lib/format';
import {
  absoluteThreadUrl,
  isOpen,
  QUEUE_SELECT,
  QUEUE_WINDOWS,
  sortByIntent,
  VIEW_LABELS,
  VIEW_ORDER,
  VIEW_STATES,
  VIEWS,
  windowDays,
  windowSince,
  type QueueView,
  type QueueWindow,
} from '../lib/queue';
import { subscribeToReviewQueue } from '../lib/realtime';
import { supabase } from '../lib/supabase';
import type { Draft, DraftState } from '../lib/types';

/**
 * The review queue.
 *
 * The whole screen exists to get one reply posted and recorded in as few moves as possible, so
 * three things were removed rather than restyled:
 *
 *   - The native dialogs. "Mark sent" opened a `window.confirm`, and rejecting opened a
 *     `window.prompt` that asked for free text. They are unstyled, the browser can suppress them,
 *     and neither could be undone. Confirmation now happens in the card, which knows something a
 *     dialog cannot: whether you actually opened this thread in this session.
 *
 *   - The extra "Approve" step. With no write API anywhere on this lane (plan.md section 2.2),
 *     approving a draft did nothing a human posting it later would not do, and it gave two ways
 *     to say yes. The queue has its two outcomes back now — but they are different endings, not
 *     two words for one: **Posted** records that a human did it, and **Auto post** queues the
 *     reply for the poster on the operator's PC (7 Oct 2026, P2.18).
 *
 * The list is split three ways. "Already decided" used to hold both drafts that really went out
 * and drafts that were taken out by hand - different things with different consequences, since
 * one wrote a delivery row and counted against the day's cap and the other posted nothing. The
 * toggle counts each view, so how much is behind the other two is visible without switching.
 *
 *   - The separate Skip and Reject links, with their reason chips. Both moved a draft to the same
 *     state and differed only by whether a free-text reason was recorded, so the operator was
 *     choosing between two words for one intention. One Remove button sits beside the post action
 *     instead, and the toast it raises has an Undo.
 */

type View = QueueView;

interface Toast {
  kind: 'ok' | 'error';
  text: string;
  /** Present when the action can be put back: the id and the state to move from. */
  undo?: { draftId: string; from: DraftState };
}

const PAGE_SIZE = 50;

export default function ReviewQueue({ actor }: { actor: string }) {
  const [rows, setRows] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>('pending');
  /** Exact per-view totals for the toggle. Null until the first count comes back. */
  const [totals, setTotals] = useState<Record<View, number> | null>(null);
  /**
   * Which platforms are toggled on. Empty means all of them: the chips are a multi-select, so
   * there is no "All" button to click back to - turning the last chip off is the way back.
   */
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  /**
   * How far back to look. Server-side - see `windowSince` in lib/queue.ts for why it cannot be
   * a filter over `rows`.
   */
  const [windowKey, setWindowKey] = useState<QueueWindow>('any');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  /**
   * The draft whose reply is being written again, and which is therefore not just "busy".
   *
   * It is separate from `busyId` because a rewrite takes seconds and rewrites the text the card is
   * showing - the button has to say "Updating…" rather than merely grey out, or the operator is
   * left wondering whether the reply they are looking at is the old one or the new one.
   */
  const [redraftingId, setRedraftingId] = useState<string | null>(null);
  /**
   * The bulk rewrite in flight: how many of how many, and nothing else.
   *
   * Kept apart from `redraftingId`, which stays per-card, because the header button shows progress
   * across the run and the closing toast counts what happened.
   */
  const [bulk, setBulk] = useState<{ done: number; total: number } | null>(null);
  /** Update all is one click over many drafts, so it arms first, like a card's Posted confirm. */
  const [bulkConfirm, setBulkConfirm] = useState(false);
  /** Drafts whose thread was opened in this session, so "posted" needs no second question. */
  const [armed, setArmed] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const load = useCallback(async (which: View, span: QueueWindow) => {
    const since = windowSince(span);
    let query = supabase
      .from('drafts')
      .select(QUEUE_SELECT)
      .in('state', VIEW_STATES[which])
      .order(VIEW_ORDER[which], { ascending: false })
      .limit(PAGE_SIZE);

    if (since) query = query.gte('created_at', since);

    const { data, error: loadError } = await query;

    if (loadError) {
      setError(loadError.message);
      setLoading(false);
      return;
    }

    setError(null);
    const next = (data ?? []) as unknown as Draft[];
    // Only work still to do gets ranked; a decided row is a record, so newest change first is right.
    setRows(which === 'pending' ? sortByIntent(next) : next);
    setLoading(false);
  }, []);

  /**
   * The numbers on the toggle.
   *
   * `head` count queries rather than `rows.length`, because the list is capped at PAGE_SIZE - the
   * count is the only thing that says how much is behind the other two views, and counting the
   * fetched rows would just read "50" forever.
   *
   * Counts are diagnostics, so a failure here is swallowed instead of raising over the queue.
   */
  const loadTotals = useCallback(async (span: QueueWindow) => {
    const since = windowSince(span);
    const results = await Promise.all(
      VIEWS.map((candidate) => {
        let query = supabase
          .from('drafts')
          .select('id', { count: 'exact', head: true })
          .in('state', VIEW_STATES[candidate]);
        if (since) query = query.gte('created_at', since);
        return query;
      }),
    );
    const next = {} as Record<View, number>;
    VIEWS.forEach((candidate, index) => {
      next[candidate] = results[index].count ?? 0;
    });
    setTotals(next);
  }, []);

  useEffect(() => {
    setLoading(true);
    void load(view, windowKey);
  }, [load, view, windowKey]);

  useEffect(() => {
    void loadTotals(windowKey);
  }, [loadTotals, windowKey]);

  /**
   * Bumped whenever the list reloads, and read by the timeline.
   *
   * The chart fetches its own counts, so it has to be told that something changed rather than
   * subscribing separately - two subscriptions would double the realtime traffic and could still
   * land a beat apart.
   */
  const [activity, setActivity] = useState(0);

  useEffect(
    () =>
      subscribeToReviewQueue({
        // Refetch rather than splice the realtime payload in: the reviewer needs the joined
        // item + source, which a drafts-only payload does not carry.
        onDraftChange: () => {
          void load(view, windowKey);
          void loadTotals(windowKey);
          setActivity((current) => current + 1);
        },
      }),
    [load, loadTotals, view, windowKey],
  );

  // Auto-dismiss. Undoable toasts stay longer, because the point of them is that the operator
  // has time to change their mind.
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), toast.undo ? 9_000 : 5_000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const platformOf = (row: Draft) => row.prospect?.item?.platform ?? 'unknown';
  const counts = useMemo(
    () =>
      rows.reduce<Record<string, number>>((accumulator, row) => {
        const key = platformOf(row);
        accumulator[key] = (accumulator[key] ?? 0) + 1;
        return accumulator;
      }, {}),
    [rows],
  );
  const platforms = useMemo(
    () => [...new Set(['facebook', ...Object.keys(counts)])],
    [counts],
  );
  const visible = useMemo(
    () =>
      platformFilter.length === 0
        ? rows
        : rows.filter((row) => platformFilter.includes(platformOf(row))),
    [rows, platformFilter],
  );

  /**
   * What "all" means for Update all: every open draft on screen, the platform chips included.
   *
   * Not every pending draft in the database - the run is one signed-in LLM call per reply, and the
   * confirmation in the header names a count, so the count has to be the list being read.
   */
  const openShown = useMemo(() => visible.filter((row) => isOpen(row.state)), [visible]);

  /** Multi-select: each chip turns one platform on or off, and off is the default. */
  const togglePlatform = useCallback((platform: string) => {
    setPlatformFilter((current) =>
      current.includes(platform)
        ? current.filter((candidate) => candidate !== platform)
        : [...current, platform],
    );
  }, []);

  const bodyOf = useCallback(
    (row: Draft) => edits[row.id] ?? row.edited_body ?? row.body,
    [edits],
  );
  const isEdited = useCallback(
    (row: Draft) => bodyOf(row).trim() !== row.body.trim(),
    [bodyOf],
  );

  async function run(row: Draft, fn: () => Promise<ActionResult>, onOk: () => void) {
    setBusyId(row.id);
    const result = await fn();
    setBusyId(null);
    if (result.ok) onOk();
    else setToast({ kind: 'error', text: result.error ?? 'Something went wrong.' });
  }

  /**
   * The whole job in one click: copy the reply, then open the thread.
   *
   * Opening a link and copying the text were two buttons, and the order was wrong either way
   * round. Clipboard write happens before the tab opens because `window.open` inside a click
   * handler is allowed and after an await is not.
   *
   * The bug this fixes: the failure path used to set an error message and then fall through to
   * the success line below it, so a failed copy reported "Reply copied." while `window.open` ran
   * anyway. The operator then pastes whatever was already on the clipboard - an old reply, a
   * password, nothing - into a live thread under their own name. The copy result now decides
   * what is said, and the card is only armed when the text really is on the clipboard.
   */
  async function openAndCopy(row: Draft) {
    const url = absoluteThreadUrl(row.prospect?.item?.platform, row.prospect?.item?.permalink ?? row.prospect?.item?.url);
    if (!url) {
      setToast({ kind: 'error', text: 'No thread link was stored for this draft.' });
      return;
    }

    let copied = false;
    try {
      await navigator.clipboard.writeText(bodyOf(row));
      copied = true;
    } catch {
      copied = false;
    }

    // Open either way: if the copy failed they can still select and copy the text by hand.
    window.open(url, '_blank', 'noopener');

    if (!copied) {
      setToast({
        kind: 'error',
        text: 'Could not copy the reply — the thread is open, so select the text and copy it by hand.',
      });
      return;
    }

    setArmed((previous) => ({ ...previous, [row.id]: new Date().toISOString() }));
    setConfirming(null);
    setToast({
      kind: 'ok',
      text: 'Reply copied. Paste it into the thread, post it, then click Posted.',
    });
  }

  async function post(row: Draft) {
    const body = bodyOf(row);
    await run(
      row,
      () => markSent(row.id, row.state, actor, body, isEdited(row)),
      () => {
        setConfirming(null);
        setArmed((previous) => {
          const next = { ...previous };
          delete next[row.id];
          return next;
        });
        setToast({ kind: 'ok', text: 'Recorded as posted. It counts against today’s cap.' });
        // The draft just moved from Pending to Posted. Realtime would catch it too, but the
        // counts are the thing this screen now promises, so do not depend on the socket.
        void loadTotals(windowKey);
      },
    );
  }

  /**
   * The other way a card ends: out of the queue, undoable, with nothing posted.
   *
   * This is the only negative action left on a card. It replaced Skip and Reject - two quiet text
   * links doing the same transition, differing only in whether a reason was recorded, which made
   * the operator choose between two words for one intention. The reason chips went with them, so
   * `rejected_reason` is now only ever set by rows that predate this change.
   */
  async function remove(row: Draft) {
    await run(
      row,
      () => removeDraft(row.id, row.state, actor),
      () => {
        setToast({
          kind: 'ok',
          text: 'Removed from the queue. It is in Rejected if you want it back.',
          undo: { draftId: row.id, from: 'skipped' },
        });
        void loadTotals(windowKey);
      },
    );
  }

  async function undo(draftId: string, from: DraftState) {
    setToast(null);
    const { error: undoError } = await reopenDraft(draftId, from, actor);
    if (undoError) setToast({ kind: 'error', text: undoError });
    else {
      setToast({ kind: 'ok', text: 'Put back in the queue.' });
      await load(view, windowKey);
      void loadTotals(windowKey);
    }
  }

  /**
   * Hand this reply to the local poster (7 Oct 2026).
   *
   * The card's Auto post button. It queues; it does not send - the poster does that from the
   * operator's PC, which is why the toast says "queued" rather than claiming anything went out.
   * The Undo is the plain one: `approved` back to `pending`, nothing else to unwind, because
   * nothing has been sent at this point.
   */
  async function queueAutoPost(row: Draft) {
    await run(
      row,
      () => queueForAutoPost(row.id, row.state, actor),
      () => {
        setToast({
          kind: 'ok',
          text: 'Queued for auto post — the local poster sends it on its next run. Nothing has gone out yet.',
          undo: { draftId: row.id, from: 'approved' },
        });
        void loadTotals(windowKey);
      },
    );
  }

  /**
   * Take a queued reply back out. No Undo on the toast: the action is its own inverse, and
   * clicking Auto post again is clearer than an undo that re-queues what was just un-queued.
   */
  async function cancelAutoPost(row: Draft) {
    await run(
      row,
      () => unqueueAutoPost(row.id, row.state, actor),
      () => {
        setToast({ kind: 'ok', text: 'Taken out of the auto post queue. Nothing was sent.' });
        void loadTotals(windowKey);
      },
    );
  }

  /**
   * The three pieces of local state a successful rewrite has to clear - see the note on `redraft`.
   * Shared with the bulk run, which has to clear the same three, one draft at a time.
   */
  const clearRedraftState = useCallback((draftId: string) => {
    setEdits((previous) => {
      const next = { ...previous };
      delete next[draftId];
      return next;
    });
    setArmed((previous) => {
      const next = { ...previous };
      delete next[draftId];
      return next;
    });
    setConfirming((previous) => (previous === draftId ? null : previous));
  }, []);

  /**
   * Write this reply again with the prompts as they are saved now.
   *
   * The Worker owns the LLM key and the write, so this is a round trip through the bridge function
   * and takes seconds - long enough that the operator must be told something is happening, which
   * is what `redraftingId` is for.
   *
   * Three pieces of local state have to be cleared when it succeeds, and each one is a way the
   * new reply could be invisible or dangerous if it were left alone:
   *
   *   - `edits`, the in-flight textarea text. Left in place it would sit on top of the reply that
   *     was just generated - the card would look unchanged, and the next blur would save the old
   *     text back over the new one.
   *   - `armed`, because the clipboard now holds the *previous* reply. An armed card records a
   *     post in one click, which is exactly the wrong thing to leave pointed at stale text.
   *   - `confirming`, so a card half-way through "did you post it?" cannot be confirmed about a
   *     reply that no longer exists.
   *
   * The row itself is refetched rather than patched: `kb_refs` and the guardrail trace are
   * rebuilt by the rewrite, and the card shows both.
   */
  async function redraft(row: Draft) {
    if (bulk || redraftingId) {
      setToast({ kind: 'error', text: 'Another rewrite is already running — wait for it to finish.' });
      return;
    }

    setRedraftingId(row.id);
    const result = await redraftDraft(row.id);
    setRedraftingId(null);

    if (!result.ok || !result.data) {
      setToast({ kind: 'error', text: result.error ?? 'Could not update the reply.' });
      return;
    }
    if (result.data.status !== 'redrafted') {
      // A refusal normally arrives as `result.error` above, because the Worker answers a refusal
      // with a non-2xx carrying `error` and the bridge keeps its words. This is the belt to that
      // brace: if a non-refusal ever comes back without the work having happened, say which state
      // it came back in rather than claiming a reply was written.
      setToast({
        kind: 'error',
        text: result.data.note ?? `The reply was not rewritten (${result.data.status}).`,
      });
      return;
    }

    clearRedraftState(row.id);

    setToast({
      kind: 'ok',
      text:
        `Reply rewritten with ${result.data.promptVersion ?? 'the current prompt'}` +
        (result.data.replacedEdit ? ' — your edit to it was replaced' : '') +
        '. Read it before posting.',
    });
    await load(view, windowKey);
  }

  /**
   * Rewrite every open reply on screen with the prompts as they are saved now.
   *
   * One `Update reply` click at a time, in a loop rather than in parallel: each draft is a
   * signed-in LLM call, and a burst is the quickest way to meet a rate limit with no way to say
   * which drafts got the old prompt. The view is refreshed once at the end rather than per draft -
   * the row is refetched by `load()` so the rebuilt guardrail trace and `kb_refs` land together,
   * and fifty refetches would stutter the list for nothing the final state does not carry.
   *
   * A failure does not stop the run: the drafts behind it are still waiting, and the closing toast
   * counts what happened with the first error.
   */
  async function redraftAll() {
    if (bulk || redraftingId) return;

    const targets = visible.filter((row) => isOpen(row.state));
    if (targets.length === 0) {
      setToast({ kind: 'error', text: 'Nothing open to update in this view.' });
      return;
    }

    setBulk({ done: 0, total: targets.length });
    let rewritten = 0;
    let replacedEdits = 0;
    let failures = 0;
    let firstError: string | null = null;

    for (const [index, row] of targets.entries()) {
      setBulk({ done: index + 1, total: targets.length });
      setRedraftingId(row.id);
      const result = await redraftDraft(row.id);
      setRedraftingId(null);

      if (!result.ok || !result.data || result.data.status !== 'redrafted') {
        failures += 1;
        if (firstError === null) {
          firstError = result.error ?? result.data?.note ?? 'the reply was not rewritten';
        }
        continue;
      }

      rewritten += 1;
      if (result.data.replacedEdit) replacedEdits += 1;
      clearRedraftState(row.id);
    }

    setBulk(null);
    await load(view, windowKey);
    void loadTotals(windowKey);

    if (rewritten === 0) {
      setToast({
        kind: 'error',
        text: `None of the ${targets.length} replies could be rewritten: ${firstError ?? 'unknown error'}.`,
      });
      return;
    }
    const summary = [`Updated ${rewritten} of ${targets.length} replies`];
    if (replacedEdits > 0) {
      summary.push(`${replacedEdits} edit${replacedEdits === 1 ? '' : 's'} replaced`);
    }
    if (failures > 0) summary.push(`${failures} could not be rewritten (${firstError})`);
    setToast({ kind: 'ok', text: `${summary.join(' — ')}. Read them before posting.` });
  }

  async function saveEdit(row: Draft) {
    const current = bodyOf(row);
    const saved = row.edited_body ?? row.body;
    if (current.trim() === saved.trim()) return; // nothing changed: no write, no audit row
    const result = await saveDraftBody(row.id, actor, current, row.body);
    if (!result.ok) setToast({ kind: 'error', text: result.error ?? 'Could not save the edit.' });
    else
      setRows((previous) =>
        previous.map((candidate) =>
          candidate.id === row.id ? { ...candidate, edited_body: current } : candidate,
        ),
      );
  }

  if (loading) return <p className="muted">Loading the queue…</p>;

  // With chips on, the list on screen is not the whole view, so say both numbers.
  const pendingHeading =
    platformFilter.length > 0
      ? `${visible.length} of ${rows.length} pending`
      : `${rows.length} pending`;

  // A count with a window applied is only honest if the window is on screen beside it: "3
  // pending" and "3 pending in the last 24 hours" are very different pieces of news.
  const spanLabel =
    windowKey === 'any'
      ? ''
      : ` (${(QUEUE_WINDOWS.find((option) => option.value === windowKey)?.label ?? '').toLowerCase()})`;

  return (
    <section>
      <div className="draft-head">
        <div>
          <h2>Review queue</h2>
          <p className="muted small">
            {view === 'pending'
              ? rows.length === 0
                ? `Nothing pending${spanLabel}.`
                : `${pendingHeading}${spanLabel} · ` +
                  platforms
                    .filter((platform) => counts[platform])
                    .map((platform) => `${platformLabel(platform)} ${counts[platform]}`)
                    .join(' · ')
              : `${rows.length} ${VIEW_LABELS[view].toLowerCase()} on this page${spanLabel}`}
          </p>
        </div>
        <div className="row-actions">
          <div>
            <label htmlFor="queue-window">Drafted</label>
            <select
              id="queue-window"
              value={windowKey}
              onChange={(event) => setWindowKey(event.target.value as QueueWindow)}
            >
              {QUEUE_WINDOWS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              void load(view, windowKey);
              void loadTotals(windowKey);
              setActivity((current) => current + 1);
            }}
          >
            Refresh
          </button>
          <button
            type="button"
            className="secondary"
            disabled={bulk !== null || redraftingId !== null || openShown.length === 0}
            title={
              openShown.length === 0
                ? 'Nothing open to update in this view.'
                : `Rewrite the ${openShown.length} open replies shown here with the prompts saved in Setup.`
            }
            onClick={() => setBulkConfirm(true)}
          >
            {bulk ? `Updating ${bulk.done}/${bulk.total}…` : 'Update all'}
          </button>
        </div>
      </div>

      {bulkConfirm ? (
        <div className="armed-line">
          <span>
            Rewrite the {openShown.length} open replies shown here with the prompts saved in Setup?
            Your edits to them are replaced.
          </span>
          <span className="grow" />
          <button
            type="button"
            className="secondary"
            onClick={() => {
              setBulkConfirm(false);
              void redraftAll();
            }}
          >
            Update all
          </button>
          <button type="button" className="link" onClick={() => setBulkConfirm(false)}>
            Cancel
          </button>
        </div>
      ) : null}

      <QueueTimeline refreshToken={activity} spanDays={windowDays(windowKey)} />

      <div className="segmented" role="group" aria-label="Which drafts to show">
        {VIEWS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-pressed={view === candidate}
            onClick={() => setView(candidate)}
          >
            {VIEW_LABELS[candidate]}
            {totals ? <span className="toggle-count">{totals[candidate]}</span> : null}
          </button>
        ))}
      </div>

      <div className="chips" role="group" aria-label="Filter by platform">
        {platforms.map((platform) => {
          const on = platformFilter.includes(platform);
          return (
            <button
              key={platform}
              type="button"
              aria-pressed={on}
              // Never disable a chip that is on: if the current view has no rows left on it, a
              // disabled chip would be a filter with no way back off.
              disabled={!on && !counts[platform]}
              onClick={() => togglePlatform(platform)}
            >
              <PlatformIcon platform={platform} size={14} />
              {platformLabel(platform)}
              <span className="toggle-count">{counts[platform] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {error ? <p className="error">{error}</p> : null}

      {rows.length === 0 ? (
        <p className="muted">
          {view === 'pending' ? (
            <>
              Nothing pending{spanLabel}. Either nothing has been found yet, or what was found
              did not clear the intent gate — that is a question about discovery, not about this
              queue.
              {windowKey !== 'any'
                ? ' Widen the window above to see what arrived earlier.'
                : ''}
            </>
          ) : view === 'posted' ? (
            'Nothing has been posted yet. A draft lands here once it is marked as posted, and it stays — this is the record of what went out and what the caps counted.'
          ) : (
            'Nothing has been rejected yet. Drafts you remove from the queue land here rather than disappearing, so a misclick is recoverable — open one and put it back.'
          )}
        </p>
      ) : visible.length === 0 ? (
        <p className="muted">
          Nothing from {platformFilter.map(platformLabel).join(' or ')} here. The counts on the chips
          above say what this view does have
          {view === 'pending'
            ? ' — and if a platform is empty there, either the watchlist has no community on it yet or nothing it returned has passed the intent gate.'
            : '.'}
        </p>
      ) : null}

      {rows.length === PAGE_SIZE ? (
        <p className="muted small">
          Showing the {PAGE_SIZE} most recent{view === 'pending' ? '' : ' changes'}. The count on the
          toggle is the real total; older drafts are in the database but not on this page.
        </p>
      ) : null}

      <div className="queue">
        {visible.map((row) => (
          <DraftCard
            key={row.id}
            row={row}
            body={bodyOf(row)}
            armed={Boolean(armed[row.id])}
            armedAt={armed[row.id] ?? null}
            confirming={confirming === row.id}
            busy={busyId === row.id}
            onEdit={(value) => setEdits((previous) => ({ ...previous, [row.id]: value }))}
            onSaveEdit={() => void saveEdit(row)}
            onOpen={() => void openAndCopy(row)}
            onAskPost={() => {
              // Armed means the last thing that happened on this card was: the reply went on the
              // clipboard and the thread opened. That is the confirmation.
              if (armed[row.id]) void post(row);
              else setConfirming(row.id);
            }}
            onCancelPost={() => setConfirming(null)}
            onPost={() => void post(row)}
            onQueueAutoPost={() => void queueAutoPost(row)}
            onCancelAutoPost={() => void cancelAutoPost(row)}
            onRedraft={() => void redraft(row)}
            redrafting={redraftingId === row.id}
            onRemove={() => void remove(row)}
            onReopen={() =>
              void run(row, () => reopenDraft(row.id, row.state, actor), () => {
                setToast({ kind: 'ok', text: 'Put back in the queue.' });
                void load(view, windowKey);
              })
            }
          />
        ))}
      </div>

      {toast ? (
        <div className={toast.kind === 'ok' ? 'toast' : 'toast error'} role="status" aria-live="polite">
          <span>{toast.text}</span>
          <span className="grow" />
          {toast.undo ? (
            <button
              type="button"
              className="link"
              onClick={() => void undo(toast.undo!.draftId, toast.undo!.from)}
            >
              Undo
            </button>
          ) : null}
          <button type="button" className="link" onClick={() => setToast(null)} aria-label="Dismiss">
            Dismiss
          </button>
        </div>
      ) : null}
    </section>
  );
}
